import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Gift, Plus, ExternalLink, Check, X } from "lucide-react";
import { toast } from "sonner";
import {getAuthHeaders, hasToken, BACKEND_URL} from "@/lib/api";

const API = `${BACKEND_URL}/api`;

// Auth config helper
const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

export default function EventWishlist({ eventId, isOwner, shareCode }) {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    title: "",
    description: "",
    amazon_url: "",
    price: ""
  });

  useEffect(() => {
    fetchWishlist();
  }, [eventId, shareCode]);

  const fetchWishlist = async () => {
    try {
      let response;
      if (shareCode) {
        // Public endpoint for guests
        response = await axios.get(`${API}/public/event/${shareCode}/wishlist`);
      } else {
        // Authenticated endpoint for owners
        response = await axios.get(`${API}/events/${eventId}/wishlist`, authConfig());
      }
      setWishlist(response.data || []);
    } catch (error) {
      console.error("Failed to fetch wishlist:", error);
      setWishlist([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    
    if (!newItem.title) {
      toast.error("Titel ist erforderlich");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/wishlist`,
        {
          ...newItem,
          price: newItem.price ? parseFloat(newItem.price) : null
        },
        authConfig()
      );
      
      toast.success("Geschenk-Wunsch hinzugefügt!");
      setNewItem({ title: "", description: "", amazon_url: "", price: "" });
      setShowAddForm(false);
      fetchWishlist();
    } catch (error) {
      console.error("Failed to add item:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen");
    }
  };

  const handleReserveItem = async (itemId) => {
    const reserverName = prompt("Dein Name (damit andere wissen, dass du es kaufst):");
    if (!reserverName) return;

    try {
      await axios.post(
        `${API}/events/${eventId}/wishlist/${itemId}/reserve`,
        { reserver_name: reserverName }
      );
      toast.success("Geschenk gekauft!");
      fetchWishlist();
    } catch (error) {
      console.error("Failed to reserve item:", error);
      toast.error(error.response?.data?.detail || "Fehler bei der Reservierung");
    }
  };

  const handleUnreserveItem = async (itemId) => {
    if (!confirm("Reservierung wirklich aufheben?")) return;

    try {
      await axios.delete(`${API}/events/${eventId}/wishlist/${itemId}/reserve`);
      toast.success("Reservierung aufgehoben");
      fetchWishlist();
    } catch (error) {
      console.error("Failed to unreserve item:", error);
      toast.error("Fehler beim Aufheben");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm("Wunsch wirklich löschen?")) return;
    
    try {
      await axios.delete(
        `${API}/events/${eventId}/wishlist/${itemId}`,
        { withCredentials: true }
      );
      toast.success("Wunsch gelöscht");
      fetchWishlist();
    } catch (error) {
      console.error("Failed to delete item:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Geschenk-Wünsche</h3>
        </div>
        
        {isOwner && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Hinzufügen
          </Button>
        )}
      </div>

      {/* Info Text */}
      {!isOwner && (
        <div className="mb-4 p-3 bg-secondary/30 rounded-lg text-sm text-muted-foreground">
          🎁 Hier findest du die Geschenk-Wünsche. Markiere ein Geschenk als gekauft, damit es nicht doppelt gekauft wird!
        </div>
      )}

      {/* Add Form (Owner only) */}
      {isOwner && showAddForm && (
        <form onSubmit={handleAddItem} className="mb-6 p-4 border rounded-lg space-y-4">
          <div>
            <Label htmlFor="title">Geschenk-Titel *</Label>
            <Input
              id="title"
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              placeholder="z.B. Kaffeemaschine"
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Beschreibung</Label>
            <Textarea
              id="description"
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              placeholder="Zusätzliche Details..."
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="amazon_url">Amazon Link (optional)</Label>
            <Input
              id="amazon_url"
              value={newItem.amazon_url}
              onChange={(e) => setNewItem({ ...newItem, amazon_url: e.target.value })}
              placeholder="https://www.amazon.de/..."
            />
          </div>

          <div>
            <Label htmlFor="price">Ungefährer Preis (€)</Label>
            <Input
              id="price"
              type="number"
              step="0.01"
              value={newItem.price}
              onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
              placeholder="z.B. 99.99"
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" className="flex-1">
              Hinzufügen
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddForm(false);
                setNewItem({ title: "", description: "", amazon_url: "", price: "" });
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      )}

      {/* Wishlist Items */}
      <div className="space-y-3">
        {wishlist.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Gift className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Noch keine Geschenk-Wünsche</p>
          </div>
        )}
        
        {wishlist.map((item) => (
          <div
            key={item.id}
            className={`p-4 rounded-lg border transition-all ${
              item.is_reserved
                ? "bg-secondary/20 border-secondary"
                : "bg-card border-border hover:border-primary"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium truncate">{item.title}</h4>
                  {item.is_reserved && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex-shrink-0">
                      ✓ Gekauft
                    </span>
                  )}
                </div>
                
                {item.description && (
                  <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
                )}
                
                {item.price && (
                  <p className="text-sm font-medium text-primary">
                    ca. {item.price.toFixed(2)} €
                  </p>
                )}
                
                {item.is_reserved && item.reserved_by && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Gekauft von: {item.reserved_by}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2 flex-shrink-0">
                {item.amazon_url && (
                  <Button
                    size="sm"
                    variant="outline"
                    asChild
                    className="h-8"
                  >
                    <a href={item.affiliate_link || item.amazon_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
                
                {!isOwner && !item.is_reserved && (
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => handleReserveItem(item.id)}
                    className="h-8"
                  >
                    Gekauft
                  </Button>
                )}
                
                {!isOwner && item.is_reserved && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUnreserveItem(item.id)}
                    className="h-8"
                  >
                    Freigeben
                  </Button>
                )}
                
                {isOwner && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteItem(item.id)}
                    className="h-8 text-destructive"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
